import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Volume2, Clock, CheckCircle, ArrowLeft, ExternalLink, Loader2, Copy, Check, XCircle, Printer } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Payment link removed

export default function BookingConfirmation() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [paymentOption, setPaymentOption] = useState("full");

  const handleStripePayment = async () => {
    try {
      const res = await axios.post(`${API}/payments/create-checkout`, {
        payment_type: "booking",
        item_id: bookingId,
        origin_url: window.location.origin
      });
      window.location.href = res.data.url;
    } catch {
      toast.error("Failed to initiate Stripe payment");
    }
  };

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data);
    } catch (error) {
      console.error("Error fetching booking:", error);
      toast.error("Request not found");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const copyBookingId = () => {
    navigator.clipboard.writeText(bookingId);
    setCopied(true);
    toast.success("Request ID copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusIcon = () => {
    switch (booking?.status) {
      case "confirmed":
        return <CheckCircle className="w-10 h-10 text-[#39FF14]" />;
      case "cancelled":
        return <XCircle className="w-10 h-10 text-[#FF0055]" />;
      default:
        return <Clock className="w-10 h-10 text-yellow-500" />;
    }
  };

  const getStatusColor = () => {
    switch (booking?.status) {
      case "confirmed":
        return "bg-[#39FF14]/20";
      case "cancelled":
        return "bg-[#FF0055]/20";
      default:
        return "bg-yellow-500/20";
    }
  };

  const getStatusTitle = () => {
    switch (booking?.status) {
      case "confirmed":
        return "Request Approved!";
      case "cancelled":
        return "Request Declined";
      case "paid":
        return "Booking Complete";
      default:
        return "Request Submitted!";
    }
  };

  const getStatusMessage = () => {
    switch (booking?.status) {
      case "confirmed":
        return "Great news! Your equipment request has been approved. Please complete the payment to secure your booking.";
      case "cancelled":
        return "Unfortunately, we couldn't fulfill your request for the selected date. Please try a different date or contact us.";
      case "paid":
        return "Thank you for renting with us! We hope you enjoyed your event.";
      default:
        return "Thank you for your request! We'll review it and get back to you shortly to confirm availability.";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#141414] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FF0033] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#141414]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            data-testid="back-home"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm uppercase tracking-wider">Home</span>
          </button>
          <div className="flex items-center gap-2">
            <img src="https://static.prod-images.emergentagent.com/jobs/641f6878-5cef-40eb-8d31-2194fc50afac/images/9204c88f36c8aba1418f399ed70957b9e9b435b9671cfdca88f8cc5e5e0a2467.png" alt="The Humble Hires" className="h-20" />
          </div>
        </div>
      </header>

      <div className="pt-32 pb-16 px-6">
        <div className="max-w-2xl mx-auto text-center">
          {/* Status Icon */}
          <div className={`w-20 h-20 mx-auto mb-8 rounded-full ${getStatusColor()} flex items-center justify-center`}>
            {getStatusIcon()}
          </div>

          <h1 className="text-3xl md:text-4xl font-bold mb-4" data-testid="confirmation-title">
            {getStatusTitle()}
          </h1>
          <p className="text-white/70 mb-8">
            {getStatusMessage()}
          </p>

          {/* Booking Details Card */}
          <div className="glass-panel p-6 md:p-8 text-left mb-8">
            <div className="flex items-center justify-between mb-6 pb-6 border-b border-white/10">
              <div>
                <p className="text-xs uppercase tracking-wider text-white/50 mb-1">Request ID</p>
                <p className="font-mono text-sm" data-testid="booking-id">{bookingId}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 text-xs uppercase tracking-wider ${
                  booking?.status === 'confirmed' ? 'status-confirmed' :
                  booking?.status === 'cancelled' ? 'status-cancelled' :
                  booking?.status === 'paid' ? 'status-completed' :
                  booking?.status === 'completed' ? 'bg-white/10 text-white/70 border border-white/20' :
                  'status-pending'
                }`}>
                  {booking?.status}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyBookingId}
                  className="text-[#FF0033] hover:text-[#FF0033] hover:bg-[#FF0033]/10"
                  data-testid="copy-booking-id"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-white/70">Equipment</span>
                <span className="font-medium">{booking?.speaker_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">Requested Date</span>
                <span>{booking?.event_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">Duration</span>
                <span>{booking?.event_duration_days} day(s)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">Quantity</span>
                <span>{booking?.quantity} unit(s)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">Delivery Address</span>
                <span className="text-right max-w-[60%]">{booking?.delivery_address}</span>
              </div>
              {booking?.special_requirements && (
                <div className="flex justify-between">
                  <span className="text-white/70">Requirements</span>
                  <span className="text-right max-w-[60%]">{booking?.special_requirements}</span>
                </div>
              )}
              {booking?.delivery_fee > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/70">Setup, Delivery & Pickup</span>
                  <span className="text-[#FF0033]">£{booking?.delivery_fee?.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between pt-4 border-t border-white/10">
                <span className="text-lg font-bold">Total Amount</span>
                <span className="text-2xl font-bold text-[#FF0033]" data-testid="total-amount">
                  £{booking?.total_price?.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Section - Only show if confirmed */}
          {booking?.status === "confirmed" && (
            <div className="glass-panel p-6 md:p-8 mb-8">
              <h2 className="text-xl font-bold mb-4">Complete Payment</h2>
              <p className="text-white/70 mb-6">
                Your request has been approved! Choose your payment option below.
              </p>

              {/* Payment Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <button
                  onClick={() => setPaymentOption("full")}
                  className={`p-4 text-left border transition-all ${
                    paymentOption === "full" 
                      ? "border-[#FF0033] bg-[#FF0033]/10" 
                      : "border-white/10 hover:border-white/30"
                  }`}
                  data-testid="payment-full"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-4 h-4 rounded-full border-2 ${paymentOption === "full" ? "border-[#FF0033] bg-[#FF0033]" : "border-white/40"}`} />
                    <span className="font-bold">Full Payment</span>
                  </div>
                  <p className="text-2xl font-bold text-[#FF0033]">£{booking?.total_price?.toFixed(2)}</p>
                  <p className="text-white/50 text-sm">Pay in full now</p>
                </button>

                <button
                  onClick={() => setPaymentOption("deposit")}
                  className={`p-4 text-left border transition-all ${
                    paymentOption === "deposit" 
                      ? "border-[#39FF14] bg-[#39FF14]/10" 
                      : "border-white/10 hover:border-white/30"
                  }`}
                  data-testid="payment-deposit"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-4 h-4 rounded-full border-2 ${paymentOption === "deposit" ? "border-[#39FF14] bg-[#39FF14]" : "border-white/40"}`} />
                    <span className="font-bold">50% Deposit</span>
                  </div>
                  <p className="text-2xl font-bold text-[#39FF14]">£{(booking?.total_price / 2).toFixed(2)}</p>
                  <p className="text-white/50 text-sm">Pay remaining 48hrs before event</p>
                </button>
              </div>
              
              {/* Bank Details */}
              <div className="bg-[#1E1E1E] border border-white/10 p-6 mb-6 text-left">
                <h3 className="text-sm uppercase tracking-wider text-[#FF0033] mb-4">Payment Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-white/70">Account Name</span>
                    <span className="font-bold">Kymani Noble</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Sort Code</span>
                    <span className="font-mono font-bold">11-05-03</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Account Number</span>
                    <span className="font-mono font-bold">17250569</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-white/10">
                    <span className="text-white/70">Amount to Pay Now</span>
                    <span className={`font-bold ${paymentOption === "deposit" ? "text-[#39FF14]" : "text-[#FF0033]"}`}>
                      £{paymentOption === "deposit" ? (booking?.total_price / 2).toFixed(2) : booking?.total_price?.toFixed(2)}
                    </span>
                  </div>
                  {paymentOption === "deposit" && (
                    <div className="flex justify-between text-yellow-500">
                      <span>Remaining (due 48hrs before)</span>
                      <span className="font-bold">£{(booking?.total_price / 2).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-white/70">Reference</span>
                    <span className="font-mono text-[#FF0033]">
                      Date of sound hire
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-xs text-white/50 mt-4">
                Please use your <span className="font-mono text-[#FF0033]">date of sound hire</span> as your payment reference
              </p>
              {paymentOption === "deposit" && (
                <p className="text-xs text-yellow-500 mt-2">
                  ⚠️ Remaining £{(booking?.total_price / 2).toFixed(2)} must be paid 48 hours before your event date
                </p>
              )}

              <Button onClick={() => window.print()} variant="outline" className="w-full mt-4 border-white/20 hover:border-white/40 font-bold" data-testid="print-booking-receipt">
                <Printer className="w-4 h-4 mr-2" />Print Receipt
              </Button>

              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-white/70 text-sm mb-3 text-center">Or pay instantly with card:</p>
                <Button
                  onClick={handleStripePayment}
                  className="w-full bg-[#635BFF] text-white hover:bg-[#635BFF]/80 font-bold h-12 text-base"
                  data-testid="pay-with-stripe"
                >
                  Pay £{paymentOption === "deposit" ? (booking?.total_price / 2).toFixed(2) : booking?.total_price?.toFixed(2)} with Stripe
                </Button>
              </div>
            </div>
          )}

          {/* Pending Message */}
          {booking?.status === "pending" && (
            <div className="glass-panel p-6 md:p-8 mb-8">
              <h2 className="text-xl font-bold mb-4">What Happens Next?</h2>
              <div className="text-left space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#FF0033]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#FF0033] font-bold text-sm">1</span>
                  </div>
                  <div>
                    <p className="font-medium">We review your request</p>
                    <p className="text-white/70 text-sm">We'll check equipment availability for your requested date</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#FF0033]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#FF0033] font-bold text-sm">2</span>
                  </div>
                  <div>
                    <p className="font-medium">You get notified</p>
                    <p className="text-white/70 text-sm">We'll contact you via email or phone to confirm</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#FF0033]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#FF0033] font-bold text-sm">3</span>
                  </div>
                  <div>
                    <p className="font-medium">Complete payment</p>
                    <p className="text-white/70 text-sm">Once approved, you can pay via bank transfer to secure your booking</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Contact Info */}
          <div className="text-center">
            <p className="text-white/70 mb-2">Questions about your request?</p>
            <p className="text-[#FF0033]">We'll be in touch soon!</p>
          </div>

          <Button
            onClick={() => navigate("/")}
            variant="outline"
            className="mt-8 btn-secondary"
            data-testid="back-to-home"
          >
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
